import "@/App.css";
import { HashRouter, Routes, Route, Navigate, useEffect } from "react-router-dom";
import { useEffect as useEff } from "react";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AdminAuthProvider, useAdminAuth } from "./contexts/AdminAuthContext";
import { UserAuthProvider } from "./contexts/UserAuthContext";
import { Navbar } from "./components/Navbar";
import { Hero } from "./components/Hero";
import { HostingTable } from "./components/HostingTable";
import { Benefits } from "./components/Benefits";
import { ListingCTA } from "./components/ListingCTA";
import { Footer } from "./components/Footer";
import { CookieBanner } from "./components/CookieBanner";
import { Impressum } from "./components/Impressum";
import { Datenschutz } from "./components/Datenschutz";
import { AdminLogin } from "./components/admin/AdminLogin";
import { AdminSetup } from "./components/admin/AdminSetup";
import { AdminProviders } from "./components/admin/AdminProviders";
import { AdminTeam } from "./components/admin/AdminTeam";
import { AdminSettings } from "./components/admin/AdminSettings";
import { AdminAudit } from "./components/admin/AdminAudit";
import { AdminApiKeys } from "./components/admin/AdminApiKeys";
import { AdminReviews } from "./components/admin/AdminReviews";
import { AdminProviderDetail } from "./components/admin/AdminProviderDetail";
import { AdminForgotPassword } from "./components/admin/AdminForgotPassword";
import { AdminResetPassword } from "./components/admin/AdminResetPassword";
import { ProviderLogin } from "./components/provider/ProviderLogin";
import { ProviderForgotPassword } from "./components/provider/ProviderForgotPassword";
import { ProviderResetPassword } from "./components/provider/ProviderResetPassword";
import { ProviderSettings } from "./components/provider/ProviderSettings";
import { ProviderDashboard } from "./components/provider/ProviderDashboard";
import { ProviderProducts } from "./components/provider/ProviderProducts";
import { ProviderApiKeys } from "./components/provider/ProviderApiKeys";
import { ProviderApiDocs } from "./components/provider/ProviderApiDocs";
import { ProviderReviews } from "./components/provider/ProviderReviews";
import ProviderDetail from "./components/ProviderDetail";
import ProductsPage from "./components/ProductsPage";
import ProductDetail from "./components/ProductDetail";
import UserLogin from "./components/user/UserLogin";
import UserRegister from "./components/user/UserRegister";
import UserAccount from "./components/user/UserAccount";
import axios from "axios";
import { useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Guard: redirect to login if not authenticated
function ProtectedRoute({ children }) {
  const { user, loading } = useAdminAuth();
  if (loading) return <div className="min-h-screen bg-[#020817] flex items-center justify-center"><div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (user.role === 'provider') return <Navigate to="/provider/dashboard" replace />;
  return children;
}

// Guard: provider only
function ProviderRoute({ children }) {
  const { user, loading } = useAdminAuth();
  if (loading) return <div className="min-h-screen bg-[#020817] flex items-center justify-center"><div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/provider/login" replace />;
  if (user.role !== 'provider') return <Navigate to="/admin/providers" replace />;
  return children;
}

// Guard: redirect to setup if no admin exists yet
function SetupGuard({ children }) {
  const [needsSetup, setNeedsSetup] = useState(null);
  useEff(() => {
    axios.get(`${API}/admin/setup/status`).then(r => setNeedsSetup(r.data.needs_setup));
  }, []);
  if (needsSetup === null) return null;
  if (needsSetup) return <Navigate to="/admin/setup" replace />;
  return children;
}

const Home = () => (
  <>
    <Navbar />
    <main>
      <Hero />
      <HostingTable />
      <Benefits />
      <ListingCTA />
    </main>
    <Footer />
  </>
);

function App() {
  return (
    <AdminAuthProvider>
      <UserAuthProvider>
        <LanguageProvider>
          <div className="App dark">
              <HashRouter>
              <Routes>
                {/* Public */}
                <Route path="/" element={<Home />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                <Route path="/produkte" element={<ProductsPage />} />
                <Route path="/anbieter/:id" element={<ProviderDetail />} />
                <Route path="/anbieter/:id/produkt/:pid" element={<ProductDetail />} />

                {/* Community user auth */}
                <Route path="/login" element={<UserLogin />} />
                <Route path="/register" element={<UserRegister />} />
                <Route path="/account" element={<UserAccount />} />

                {/* Admin auth */}
                <Route path="/admin/setup" element={<AdminSetup />} />
                <Route path="/admin/login" element={
                  <SetupGuard><AdminLogin /></SetupGuard>
                } />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route path="/reset-password" element={<AdminResetPassword />} />

                {/* Admin protected */}
                <Route path="/admin" element={<Navigate to="/admin/providers" replace />} />
                <Route path="/admin/providers" element={
                  <ProtectedRoute><AdminProviders /></ProtectedRoute>
                } />
                <Route path="/admin/providers/:id" element={
                  <ProtectedRoute><AdminProviderDetail /></ProtectedRoute>
                } />
                <Route path="/admin/api-keys" element={
                  <ProtectedRoute><AdminApiKeys /></ProtectedRoute>
                } />
                <Route path="/admin/reviews" element={
                  <ProtectedRoute><AdminReviews /></ProtectedRoute>
                } />
                <Route path="/admin/team" element={
                  <ProtectedRoute><AdminTeam /></ProtectedRoute>
                } />
                <Route path="/admin/audit" element={
                  <ProtectedRoute><AdminAudit /></ProtectedRoute>
                } />
                <Route path="/admin/settings" element={
                  <ProtectedRoute><AdminSettings /></ProtectedRoute>
                } />

                {/* Provider portal */}
                <Route path="/provider/login" element={<ProviderLogin />} />
                <Route path="/provider/forgot-password" element={<ProviderForgotPassword />} />
                <Route path="/provider/reset-password" element={<ProviderResetPassword />} />
                <Route path="/provider" element={<Navigate to="/provider/dashboard" replace />} />
                <Route path="/provider/dashboard" element={
                  <ProviderRoute><ProviderDashboard /></ProviderRoute>
                } />
                <Route path="/provider/products" element={
                  <ProviderRoute><ProviderProducts /></ProviderRoute>
                } />
                <Route path="/provider/reviews" element={
                  <ProviderRoute><ProviderReviews /></ProviderRoute>
                } />
                <Route path="/provider/api-keys" element={
                  <ProviderRoute><ProviderApiKeys /></ProviderRoute>
                } />
                <Route path="/provider/api-docs" element={
                  <ProviderRoute><ProviderApiDocs /></ProviderRoute>
                } />
                <Route path="/provider/settings" element={
                  <ProviderRoute><ProviderSettings /></ProviderRoute>
                } />
              </Routes>
              <CookieBanner />
          </HashRouter>
          </div>
        </LanguageProvider>
      </UserAuthProvider>
    </AdminAuthProvider>
  );
}

export default App;
